/* eslint-disable max-len,import/newline-after-import,no-restricted-globals */
import tinymce from 'tinymce';
import 'tinymce/themes/silver';
import EventBus from '@/EventBus';
import ApplicationEventTypes from '@/mixins/ApplicationEventTypes';
import { GUIToTinyMCECommandsDictionary, TextCommandTypes } from '@/mixins/TextCommandTypes';
import twemoji from 'twemoji';
import { decodeStoredHtml, removeInlineColors } from '@/components/Helper';
import {
  findObjectById,
  findParentGroup,
  recalculateGroupBounds,
  getPropertyApplyTargets,
} from '@/components/Canvas/utils/groupHelpers';

export default {
  methods: {
    isTextEditModeActive() {
      if (this.activeObject && this.activeObject.type === 'text' && this.activeObject.isEditorEnable) {
        return true;
      }
      return !!document.querySelector('.focus-text-editor');
    },
    hideMoveableDuringTextEdit() {
      if (!this.moveableObj) { return; }
      this.moveableObj.target = null;
      if (typeof this.moveableObj.updateTarget === 'function') {
        this.moveableObj.updateTarget();
      }
      if (typeof this.hideBoundingBox === 'function') {
        this.hideBoundingBox(true);
      }
    },
    async setupActiveEditor() {
      const targetId = this.activeObject.Id;
      const target   = document.getElementById(targetId);
      if (!this.allTextEditorList[targetId]) { this.activeEditor = await this.enableEditor(target.firstElementChild); } else { this.activeEditor = this.allTextEditorList[targetId]; }

      if (this.activeEditor.targetElm.textContent.toLowerCase() == 'enter text here') {
        try {
          this.activeEditor.selection.select(this.activeEditor.targetElm, true);
        } catch (ex) {
          console.log(ex);
        }
      }

      const textDiv        = document.getElementById(`editor-${targetId}`);
      const selectedObject = findObjectById(this.contentList, targetId)
          || findObjectById(this.objectList, targetId);
      if (selectedObject && selectedObject.unCurvedHtml && selectedObject.shapeType && selectedObject.shapeType === 'curve') {
        const decoded = decodeStoredHtml(selectedObject.unCurvedHtml);
        selectedObject.Value        = decoded;
        selectedObject.modifiedHtml = decoded;
        textDiv.innerHTML           = decoded;

        if (selectedObject.nonCurvedWidth != null && selectedObject.nonCurvedHeight != null) {
          selectedObject.Width  = selectedObject.nonCurvedWidth;
          selectedObject.Height = selectedObject.nonCurvedHeight;
          // Step back onto the flat box without leaving the spot the object was moved to.
          selectedObject.X               -= selectedObject.nonCurvedOffsetX || 0;
          selectedObject.Y               -= selectedObject.nonCurvedOffsetY || 0;
          selectedObject.nonCurvedOffsetX = 0;
          selectedObject.nonCurvedOffsetY = 0;
          if (target) {
            target.style.left   = `${selectedObject.X}px`;
            target.style.top    = `${selectedObject.Y}px`;
            target.style.width  = `${selectedObject.Width}px`;
            target.style.height = `${selectedObject.Height}px`;
          }
          const parentGroup = findParentGroup(this.objectList || this.contentList, targetId);
          if (parentGroup) {
            recalculateGroupBounds(parentGroup);
          }
        }
      }
      textDiv.contentEditable = true;
      textDiv.focus();
      target.classList.add('focus-text-editor');
      this.activeObject.isEditorEnable  = true;
      this.hideMoveableDuringTextEdit();
      if (this.selectO) {
        this.selectO.toggleContinueSelect = '';
      }
    },
    enableEditor(target) {
      const editorList           = this.allTextEditorList;
      const debounceMethod       = _.debounce(() => EventBus.$emit('GetTextSelectionProperties'), 300);
      const parseTwemojiInEditor = (ed) => {
        try {
          const root = (ed && typeof ed.getBody === 'function' && ed.getBody()) || (ed && ed.targetElm) || null;
          if (!root) { return; }
          twemoji.parse(root, {
            className:  'emoji',
            callback:   icon => `https://cdn.jsdelivr.net/gh/twitter/twemoji@14.0.2/assets/svg/${icon}.svg`,
            attributes: () => ({
              draggable:   'false',
              crossorigin: 'anonymous',
              style:       'display:inline-block;width:1em;height:1em;max-width:1em;line-height:1;vertical-align:-0.15em;margin:0 0.05em;pointer-events:none;',
            }),
          });
        } catch (ex) {
          console.log(ex);
        }
      };
      if (tinyMCE.get(target.id)) {
        tinyMCE.get(target.id).destroy();
      }
      return new Promise((resolve, reject) => {
        tinymce.init({
          target,
          browser_spellcheck:      true,
          inline:                  true,
          menubar:                 false,
          toolbar:                 false,
          skin:                    false,
          theme:                   false,
          extended_valid_elements: 'font[style]',
          formats:                 {
            alignleft: [
              { selector: 'li', classes: 'li-left-align', styles: { 'text-align': 'left' } },
              { selector: 'p,h1,h2,h3,h4,h5,h6,td,th,div,ul,ol,table,img,audio,video', styles: { 'text-align': 'left' } },
            ],
            aligncenter: [
              { selector: 'li', classes: 'li-center-align', styles: { 'text-align': 'center' } },
              { selector: 'p,h1,h2,h3,h4,h5,h6,td,th,div,ul,ol,table,img,audio,video', styles: { 'text-align': 'center' } },
            ],
            alignright: [
              { selector: 'li', classes: 'li-right-align', styles: { 'text-align': 'right' } },
              { selector: 'p,h1,h2,h3,h4,h5,h6,td,th,div,ul,ol,table,img,audio,video', styles: { 'text-align': 'right' } },
            ],
            alignfull: [
              { selector: 'li', classes: 'li-justify-align', styles: { 'text-align': 'justify' } },
              { selector: 'p,h1,h2,h3,h4,h5,h6,td,th,div,ul,ol,table,img,audio,video', styles: { 'text-align': 'justify' } },
            ],
            underline: { inline: 'u' },
          },
        })
            .then((editor) => {
              if (!editor[0]) { return; }
              const ed = editor[0];
              ed.on('selectionChange', () => {
                if (ed.hasFocus()) { debounceMethod(); }
              });
              ed.on('input', () => {
                if (ed.hasFocus()) { parseTwemojiInEditor(ed); }
              });
              ed.on('BeforeAddUndo', () => false); // disabling tinymce Undo
              ed.on('Paste', (e) => {
                if (ed.hasFocus()) {
                  e.preventDefault();
                  const content = ((e.originalEvent || e).clipboardData || window.clipboardData).getData('Text');
                  ed.execCommand('mceInsertContent', false, content);
                  setTimeout(() => parseTwemojiInEditor(ed), 0);
                }
              }); // disabling tinymce Undo
              editorList[target.parentNode.id] = ed;
              EventBus.$emit(ApplicationEventTypes.ProductLoadingEvents.UPDATE_HTML_AFTER_ENABLE_EDITOR, {
                html: ed.getContent(),
                id:   target.parentNode.id,
              });
              return resolve(ed);
            })
            .catch((err) => {
              console.log('error in initializing tinyMCE', err);
              reject(err);
            });
      });
    },
    checkBasicConditions(value) {
      // TODO: Always send string yo this function
      if (value === undefined || value === null || (typeof value === 'string' && !value.length > 0) || (typeof value === 'number' && isNaN(value))) { return false; }
      return true;
    },
    // TODO: Convert to Const & Mixin
    async onUpdateTextProperties(data) {
      const targets = getPropertyApplyTargets(
          this.contentList || this.objectList || (this.product && this.product.pages[this.activePage].objectList),
          this.selectedObjectsList,
          this.activeObject,
      );

      const update = (obj) => {
        if (!obj) { return; }
        // Skip non-text when applying from a group / multi-select flatten list.
        if (obj.type && obj.type !== 'text') { return; }
        this.onUpdateTextPropertiesById(data, obj.Id);
      };

      if (targets.length) {
        await Promise.all(targets.map(update));
      } else if (this.activeObject) {
        update(this.activeObject);
      }

      setTimeout(() => {
        EventBus.$emit(ApplicationEventTypes.ObjectSelectionEvents.UPDATED);
      }, 5);

      if (data.command === 'forecolor') {
        setTimeout(() => {
          EventBus.$emit('updateProductColor');
        }, 500);
      }
    },
    async onUpdateTextPropertiesById(data, objectId) {
      const object = findObjectById(this.product.pages[this.activePage].objectList, objectId);
      if (!object || object.type !== 'text') { return; }

      const isTextType = object.type === 'text';
      const isTinyMce  = data.commandType === TextCommandTypes.TinyMceCommand;

      const tString = object.Id;
      const target  = document.getElementById(tString);

      if (isTextType && this.activeEditor && isTinyMce) {
        if (data.command === 'FontName' && this._fontPreviewState) {
          this.clearFontFamilyPreviewOnCanvas();
        }
        // this.activeEditor = await this.enableEditor(target.firstElementChild);
        this.activeEditor.execCommand(data.command, false, data.value);

        if (['JustifyCenter', 'JustifyRight', 'JustifyLeft', 'JustifyFull'].includes(data.command)) {
          this.changeHorizontalAlign({ command: data.command }, object);
        }

        if (['FontSize', 'LineHeight', 'FontName'].includes(data.command)) {
          this.setParentHeightAccordingToText(object);
        }

        if (
            this.isWholeTextSelected(this.activeEditor)
            && ['forecolor', 'FontSize', 'FontName', 'LineHeight'].includes(data.command)
        ) {
          const propertyName                      = GUIToTinyMCECommandsDictionary[data.command];
          object.valuesForLeftPanel[propertyName] = data.value;
          object[propertyName]                    = data.value;
        }

        const { tagName } = this.activeEditor.selection.getNode();
        if (data.command === 'Bold' && !data.value && tagName === 'SPAN') {
          this.removeAllPendingTag(this.activeEditor.selection.getNode(), 'strong');
        }
        if (data.command === 'Underline' && !data.value && tagName === 'SPAN') {
          this.removeAllPendingTag(this.activeEditor.selection.getNode(), 'u');
        }

        this.applyStyleToLi();
      }
      if (this.selectedObjectsList.length > 1 && this.activeEditor) {
        this.activeEditor = await this.enableEditor(target.firstElementChild);
      }


      if (!this.activeEditor || !isTinyMce) {
        object && this.applyPropertiesToParent(data, object.Id);
      }
      console.log('Update curve text color here');
    },
    removeAllPendingTag(element, tag) {
      const strongList = element.querySelectorAll(tag);
      Array.prototype.forEach.call(strongList, (strong) => {
        strong.outerHTML = strong.innerHTML;
      });
      this.activeEditor.selection.select(element);
    },
    addingTextEditor({ id, editor }) {
      editor && (this.allTextEditorList[id] = editor);
    },
    previewFontFamilyOnCanvas(fontName) {
      if (!fontName) { return; }
      if (this.activeObject && this.activeObject.type === 'table') {
        return;
      }
      if (this.activeEditor && !this.isWholeTextSelected(this.activeEditor)) {
        return;
      }
      const normalized = (typeof fontName === 'string' ? fontName : '').replace(/"/g, '').split(',')[0].trim();
      if (!normalized) { return; }

      const ids = (this.selectedObjectsList && this.selectedObjectsList.length > 0)
          ? this.selectedObjectsList.map(o => o && o.Id).filter(Boolean)
          : (this.activeObject && this.activeObject.Id ? [this.activeObject.Id] : []);

      if (!ids.length) { return; }

      const previewValue = JSON.stringify(normalized);

      if (!this._fontPreviewState) {
        this._fontPreviewState = new Map();
      }

      ids.forEach((id) => {
        const wrapper = document.getElementById(id)
            || document.querySelector(`.canvas-non-play-all [id='${id}']`)
            || document.querySelector(`.canvas-main-html-container [id='${id}']`);

        if (!wrapper) { return; }

        const elements = [
          wrapper,
          ...wrapper.querySelectorAll('*'),
        ];

        elements.forEach((el) => {
          if (!this._fontPreviewState.has(el)) {
            this._fontPreviewState.set(el, {
              el,
              originalInline:   el.style.fontFamily,
              originalComputed: window.getComputedStyle(el).getPropertyValue('font-family'),
            });
          }

          el.style.fontFamily = previewValue;
        });
      });
    },
    clearFontFamilyPreviewOnCanvas() {
      if (!this._fontPreviewState) { return; }

      this._fontPreviewState.forEach(({ el, originalInline }) => {
        if (!el) { return; }

        if (originalInline && originalInline.length) {
          el.style.fontFamily = originalInline;
        } else {
          el.style.removeProperty('font-family');
        }
      });

      this._fontPreviewState.clear();
    },
    async applyPropertiesToParent(data, objectId = '') {
      console.log('Applying propert to parent');
      const activeObject = objectId !== '' ? findObjectById(this.contentList, objectId) : this.activeObject;
      const propertyName = GUIToTinyMCECommandsDictionary[data.command];

      let { value } = data;
      if (data.command === 'FontSize' && this.selectedObjectsList.length > 1) {
        if (data.buttonType === 'increment') {
          value = `${parseFloat(activeObject.fontSize) + 1}px`;
        } else if (data.buttonType === 'decrement') {
          value = `${parseFloat(activeObject.fontSize) - 1}px`;
        } else {
          // If no event type is provided, use the value directly
        }
      }
      if (data.command === 'FontName') {
        this.clearFontFamilyPreviewOnCanvas();
      }
      if (data.commandType === TextCommandTypes.VerticalAlignmentCommand) {
        activeObject[data.command]                    = data.value;
        activeObject.valuesForLeftPanel[data.command] = data.value;
      } else if (data.commandType === TextCommandTypes.LineHeightCommand) {
        activeObject.valuesForLeftPanel[propertyName] = data.value;
        activeObject[propertyName]                    = data.value;
      } else {
        console.log('targetIdtargetId', data, objectId, this.targets);

        const target   = objectId !== '' ? document.getElementById(objectId) : this.targets[0];
        const targetId = target.id;
        if (!this.allTextEditorList[targetId]) {
          this.activeEditor = await this.enableEditor(target.firstElementChild);
        } else {
          this.activeEditor = this.allTextEditorList[targetId];
        }
        const { activeEditor } = this;
        if (activeEditor) {
          activeEditor.selection.select(activeEditor.targetElm, true);
          activeEditor.execCommand(data.command, false, value);
          if (data.command === 'forecolor') {
            this.applyStyleToFontTag({
              property: 'color',
              value:    data.value,
            }, this.activeEditor.targetElm.parentNode);
          }
          data.command === 'FontSize' && this.applyStyleToFontTag({
            property: 'fontSize',
            value,
          }, this.activeEditor.targetElm.parentNode);
          data.command === 'FontName' && this.applyStyleToFontTag({
            property: 'fontFamily',
            value:    data.value,
          }, this.activeEditor.targetElm.parentNode);


          this.applyStyleToLi();
          activeEditor.selection.collapse();
        }
        target.firstElementChild.blur();
        this.activeEditor                             = null;
        activeObject.valuesForLeftPanel[propertyName] =  value;
        // activeObject[propertyName]                    = data.value;
        if (propertyName === 'fontSize') {
          activeObject[propertyName] = value;
          if (data.command === 'FontSize' && this.selectedObjectsList.length > 1) {
            if (data.buttonType === '') {
              activeObject.scaleX = 1;
              activeObject.scaleY = 1;
            }
          }
        }
        if (propertyName === 'fontFamily' || propertyName === 'color' || propertyName === 'lineSpacing') {
          activeObject[propertyName] = data.value;
        }
        if (propertyName === 'fontFamily' && activeObject.shapeType == 'curve' && activeObject.unCurvedHtml != '') {
          activeObject.unCurvedHtml = this.removeInlineFontFamily(activeObject.unCurvedHtml);
        }
        if (propertyName === 'color' && activeObject.shapeType == 'curve' && activeObject.unCurvedHtml) {
          activeObject.unCurvedHtml = this.removeInlineColor(activeObject.unCurvedHtml);
        }
        (data.command === 'JustifyLeft' || data.command === 'JustifyCenter' || data.command === 'JustifyFull' || data.command === 'JustifyRight') && this.changeHorizontalAlign(data, activeObject);
        if (activeObject) {
          this.setParentHeightAccordingToText(activeObject);
          const editor              = document.getElementById(`editor-${target.Id}`);
          activeObject.modifiedHtml = activeObject.Value  = (editor && editor.querySelectorAll('li').length) ? editor.innerHTML : activeEditor.getContent();// tinymce isn't
          // activeObject.modifiedHtml = activeEditor.getContent();
        }
      }
      setTimeout(() => {
        this.emitUndoRedoRequest('called by text property change');
      }, 10);
    },
    removeInlineFontFamily(html) {
      const wrapper     = document.createElement('div');
      wrapper.innerHTML = html;

      // Select all elements that have inline styles
      wrapper.querySelectorAll('[style]').forEach((el) => {
        const style = el.getAttribute('style');

        // Remove font-family from the inline style
        const updated = style
            .split(';')
            .map(s => s.trim())
            .filter(s => !s.toLowerCase().startsWith('font-family'))
            .join('; ');

        if (updated.trim()) {
          el.setAttribute('style', updated);
        } else {
          el.removeAttribute('style');
        }
      });

      return wrapper.innerHTML;
    },
    removeInlineColor(html) {
      return removeInlineColors(html);
    },
    isWholeTextSelected(activeEditor) {
      const selectedText = activeEditor.selection.getContent({ format: 'text' }).replaceAll('\n', '').trim();
      const wholeText    = activeEditor.targetElm.innerText.replaceAll('\n', '').trim();
      return wholeText == selectedText;
    },
    changeHorizontalAlign(data, textObject = null) {
      const target = textObject || this.activeObject;
      if (!target || target.type !== 'text' || !target.valuesForLeftPanel) { return; }

      target.valuesForLeftPanel.isJustifyLeft   = target.isJustifyLeft   = data.command === 'JustifyLeft';
      target.valuesForLeftPanel.isJustifyRight  = target.isJustifyRight  = data.command === 'JustifyRight';
      target.valuesForLeftPanel.isJustify       = target.isJustify       = data.command === 'JustifyFull';
      target.valuesForLeftPanel.isJustifyCenter = target.isJustifyCenter = data.command === 'JustifyCenter';
    },
    removeTagsStyle(selectedElement) {
      const allStyleTags = selectedElement.querySelectorAll('U , STRONG , EM');
      if (allStyleTags.length) {
        for (let t = 0; t < allStyleTags.length; t++) {
          if (allStyleTags[t].style) {
            allStyleTags[t].removeAttribute('style');
          }
        }
      }
    },
    applyStyleToLi() {
      if (this.activeEditor) {
        const selectedElement = this.activeEditor.targetElm.parentNode;
        const selectedTag     = this.activeEditor.selection.getNode();

        if (selectedElement) {
          this.removeTagsStyle(selectedElement);
          const allLi       = selectedElement.getElementsByTagName('LI');
          const paddingLeft = 0;
          if (allLi.length) {
            for (let i = 0; i < allLi.length; i++) {
              const liInnerSpan = allLi[i].getElementsByTagName('SPAN');
              if (liInnerSpan) {
                const computedStyles = liInnerSpan.length ? window.getComputedStyle(liInnerSpan[0]) : null;
                if (computedStyles) {
                  allLi[i].style.color      = computedStyles.getPropertyValue('color');
                  allLi[i].style.fontSize   = computedStyles.getPropertyValue('font-size');
                  allLi[i].style.fontFamily = computedStyles.getPropertyValue('font-family');
                  // allLi[i].style.lineHeight            = computedStyles.getPropertyValue('line-height');
                  allLi[i].parentNode.style.lineHeight =  parseFloat(computedStyles.getPropertyValue('line-height')) / parseFloat(allLi[i].style.fontSize);
                  allLi[i].parentNode.style.fontSize   =   computedStyles.getPropertyValue('font-size');
                  allLi[i].dataset.mceStyle            = `color: ${allLi[i].style.color}; font-size: ${allLi[i].style.fontSize}; font-family: ${allLi[i].style.fontFamily}; list-style-position: ${allLi[i].style.listStylePosition}; word-break: ${allLi[i].style.wordBreak};line-height: ${allLi[i].parentNode.style.lineHeight};`;
                  if (selectedTag.isSameNode(allLi[i].parentNode.parentNode)) {
                    allLi[i].parentNode.parentNode.style.fontSize = allLi[i].style.fontSize;
                  }
                }
              }
            }
          }
        }
      }
    },
    applyStyleToFontTag(data, selectedElement) {
      console.log('applyStyleToFontTag', data, selectedElement);
      if (this.activeEditor && data) {
        if (selectedElement) {
          const fonts = selectedElement.getElementsByTagName('font');
          for (let fontIndex = 0; fontIndex < fonts.length; fontIndex++) {
            console.log('fonts[fontIndex]', fonts[fontIndex]);
            fonts[fontIndex].style[data.property] = data.value;
          }
        }
      }
    },
    findPrentSubSup(node, id) {
      if (node && (node.tagName === 'SUP' || node.tagName === 'SUB')) { return true; } if (node && node.id === id) { return false; }
      return this.findPrentSubSup(node.parentNode, id);
    },
    getSelectedNode(activeEditor) {
      if (activeEditor) {
        if (this.isWholeTextSelected(activeEditor)) { return tinyMCE.DOM.get(this.activeObject.Id).children[0]; }
        return activeEditor.selection.getNode();
      }

      return tinyMCE.DOM.get(this.activeObject.Id).children[0];
    },
    getTextSelectionProperties() {
      const i                              = 0;
      const { activeEditor, activeObject } = this;
      if (!activeObject) { return; }
      if (tinyMCE.DOM.get(activeObject.Id).style.overflowY !== 'scroll') { this.setParentHeightAccordingToText(activeObject); }
      const node = this.getSelectedNode(activeEditor);// activeEditor  ? ((this.isWholeTextSelected(activeEditor) ? tinyMCE.DOM.get(activeObject.Id).children[0] : activeEditor.selection.getNode()) : tinyMCE.DOM.get(activeObject.Id).children[0];

      const list        = tinyMCE.DOM.getParent(node, 'ul,ol') || (node && node.querySelector && node.querySelector('ul,ol'));
      const isBold      = (activeEditor) ? activeEditor.queryCommandState('Bold') : (parseInt(tinyMCE.DOM.getStyle(node, 'font-weight', true)) > 400);
      const isItalic    = (activeEditor) ? activeEditor.queryCommandState('Italic') : (tinyMCE.DOM.getStyle(node, 'font-style', true) === 'italic');
      const isUnderline = (activeEditor) ? activeEditor.queryCommandState('Underline') : (tinyMCE.DOM.getStyle(node, 'text-decoration', true).includes('underline'));
      let fontSize      =  parseFloat(tinyMCE.DOM.getStyle(node, 'font-size', true)); // (activeEditor) ? parseFloat(activeEditor.queryCommandValue('FontSize')) :
      const lineSpacing = parseFloat(tinyMCE.DOM.getStyle(node, 'line-height', true)) / fontSize;
      if (this.findPrentSubSup(node, activeObject.Id)) {
        fontSize = parseFloat(activeEditor.queryCommandValue('FontSize'));
      }
      const selectedColor = (activeEditor) ? activeEditor.queryCommandValue('ForeColor') : tinyMCE.DOM.getStyle(node, 'color', true);
      const temp          = {
        fontFamily:      tinyMCE.DOM.getStyle(node, 'font-family', true),
        fontSize,
        lineSpacing,
        color:           selectedColor,
        isBold,
        isItalic,
        isUnderline,
        isJustifyCenter: tinyMCE.DOM.getStyle(node, 'text-align', true) === 'center',
        isJustifyLeft:   tinyMCE.DOM.getStyle(node, 'text-align', true) === 'left',
        isJustifyRight:  tinyMCE.DOM.getStyle(node, 'text-align', true) === 'right',
        isJustify:       tinyMCE.DOM.getStyle(node, 'text-align', true) === 'justify',
        isBulletList:    !!((list && list.tagName === 'UL')),
        isNumberList:    !!(list && list.tagName === 'OL'),
        isSubScript:     !!(((activeEditor) && activeEditor.queryCommandState('subscript'))),
        isSuperScript:   !!(((activeEditor) && activeEditor.queryCommandState('superscript'))),
      };

      this.$nextTick(() => {
        activeObject.valuesForLeftPanel = temp;
        let tempContent                 = '';
        activeEditor && (tempContent = activeEditor.getContent());

        if (activeEditor && activeObject && (tempContent !== activeObject.Value || tempContent !== activeObject.modifiedHtml)) {
          activeObject.modifiedHtml = tempContent;
          activeObject.Value        = tempContent;
          setTimeout(() => {
            this.emitUndoRedoRequest('called by text property change');
          }, 10);
        }
      });
    },
    setParentHeightAccordingToText(activeObject) {
      const element = document.getElementById(activeObject.Id);
      if (!element || !element.firstElementChild) { return; }

      const parentComputedHeight = window.getComputedStyle(element).getPropertyValue('height');
      const childComputedHeight  = window.getComputedStyle(element.firstElementChild).getPropertyValue('height');

      if (parseInt(parentComputedHeight, 10) < parseInt(childComputedHeight, 10)) {
        activeObject.Height = parseInt(childComputedHeight, 10) + 1;

        const parentGroup = findParentGroup(this.objectList || this.contentList, activeObject.Id);
        if (parentGroup) {
          recalculateGroupBounds(parentGroup);
        }
        this.refreshMoveableTargets();

        setTimeout(() => {
          EventBus.$emit(ApplicationEventTypes.ObjectSelectionEvents.UPDATED);
        }, 100);
      }
    },
    setParentHeightAccordingToChildElement(activeObject) {
      const element = document.getElementById(activeObject.Id);
      if (!element || !element.firstElementChild) { return; }

      const parentComputedHeight = window.getComputedStyle(element).getPropertyValue('height');
      const childComputedHeight  = window.getComputedStyle(element.firstElementChild).getPropertyValue('height');

      if (parseInt(parentComputedHeight, 10) > parseInt(childComputedHeight, 10)) {
        activeObject.Height = parseInt(childComputedHeight, 10) + 1;

        const parentGroup = findParentGroup(this.objectList || this.contentList, activeObject.Id);
        if (parentGroup) {
          recalculateGroupBounds(parentGroup);
        }
        this.refreshMoveableTargets();
        setTimeout(() => {
          EventBus.$emit(ApplicationEventTypes.ObjectSelectionEvents.UPDATED);
        }, 100);
      }
    },
    emitUndoRedoRequest(data) {
      EventBus.$emit('addIntoPageWiseUndoRedoArray', data);
    },
  },
};
